import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Pencil, Check, X, Power, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useWarehouses } from "../lib/useWarehouses";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

// iter 275: Settings → Sample Warehouses. Admin adds / renames / toggles /
// deletes warehouses. Only "name" is required.
export default function SampleWarehousesPanel() {
  const { t } = useTranslation();
  const { warehouses, reload } = useWarehouses();
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); await reload(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); }
    finally { setBusy(false); }
  };
  const add = () => { if (!newName.trim()) return; run(async () => { await api.post("/sample-warehouses", { name: newName.trim() }); setNewName(""); }, t("warehouse.created")); };
  const rename = (w) => run(async () => { await api.put(`/sample-warehouses/${w.id}`, { name: editName.trim() }); setEditing(null); }, t("warehouse.renamed"));
  const toggle = (w) => run(() => api.put(`/sample-warehouses/${w.id}`, { active: !w.active }));
  const remove = (w) => { if (!window.confirm(t("warehouse.delete_confirm", { name: w.name }))) return; run(() => api.delete(`/sample-warehouses/${w.id}`), t("warehouse.deleted")); };

  return (
    <div className="space-y-6" data-testid="sample-warehouses-section">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">{t("warehouse.title")}</h1>
        <p className="text-sm text-gray-500 mt-1">{t("warehouse.subtitle")}</p>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4" data-testid="sample-warehouses-card">
        <div className="flex items-center gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={t("warehouse.name_placeholder")} className="h-9 w-64" data-testid="warehouse-new-name" />
          <Button size="sm" className="h-9" onClick={add} disabled={busy || !newName.trim()} data-testid="warehouse-add-button"><Plus size={14} className="mr-1" />{t("warehouse.add")}</Button>
        </div>
        <table className="w-full" data-testid="warehouse-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-[10px] uppercase tracking-label text-gray-500 font-semibold px-3 py-2 text-left">{t("warehouse.name")}</th>
              <th className="text-[10px] uppercase tracking-label text-gray-500 font-semibold px-3 py-2 text-left">{t("warehouse.status")}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {warehouses.map((w) => (
              <tr key={w.id} className="border-t border-gray-100" data-testid={`warehouse-row-${w.id}`}>
                <td className="px-3 py-2 text-sm text-gray-900">
                  {editing === w.id ? (
                    <div className="flex items-center gap-1">
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 w-56" autoFocus data-testid="warehouse-edit-name" />
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => rename(w)} data-testid="warehouse-edit-save"><Check size={14} /></Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditing(null)}><X size={14} /></Button>
                    </div>
                  ) : (
                    <span className="flex items-center gap-2">{w.name}{w.is_default && <span className="text-[10px] px-1.5 rounded bg-amber-50 text-amber-700 border border-amber-200">{t("warehouse.default_badge")}</span>}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs"><span className={`px-1.5 py-0.5 rounded border ${w.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}>{w.active ? t("warehouse.active") : t("warehouse.inactive")}</span></td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditing(w.id); setEditName(w.name); }} data-testid={`warehouse-rename-${w.id}`}><Pencil size={13} /></Button>
                    {!w.is_default && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title={w.active ? t("warehouse.deactivate") : t("warehouse.activate")} onClick={() => toggle(w)} data-testid={`warehouse-toggle-${w.id}`}><Power size={13} className={w.active ? "text-emerald-600" : "text-gray-400"} /></Button>}
                    {!w.is_default && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-600" onClick={() => remove(w)} data-testid={`warehouse-delete-${w.id}`}><Trash2 size={13} /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
